/*
  Product compare on a collection or search page: the checkbox of every card, the switch in the toolbar, the tab and the
  tray (sections/product-compare.liquid), and the shopper's selection they share.

  The selection is a list of products in the order they were ticked, each kept as its id, handle and title in the
  browser (compare-items). Reading it never throws: damaged or blocked storage gives an empty list, and a list
  longer than the current limit is cut. A change in another tab is followed, the switch's too. In the theme editor
  nothing is read from or written to the browser's storage. Every change goes out as the 'compare-change' event of
  assets/constants.js (pubsub), which the checkboxes, the tab and the tray follow: the tab shows while something is
  ticked, and the tray closes once nothing is.

  Each product is asked for once per variant, with one Section Rendering request that returns its tray card
  (sections/compare-tray.liquid) and its table cells (the compare section), and the answer is kept until the page
  goes (never in the theme editor). A request that answers 404, or with no product, takes the product out of the
  selection; any other failure keeps it and offers another try. An answer that arrives after the tray was closed or
  filled again is dropped. The table (assets/compare-table.js) loads when it first opens, with the files it needs.

  Whether compare is on is one attribute of the page, data-compare-mode="on", set before the cards paint
  (snippets/compare-assets.liquid) and kept here under the key that snippet writes on this script's tag.
*/

if (!customElements.get('product-compare')) {
  const ITEMS_KEY = 'compare-items';
  const MODE_KEY = document.querySelector('script[data-mode-key]').dataset.modeKey;
  // The answers kept for the page, one per product and variant.
  const answers = sectionAnswers({ size: 30 });
  const TOAST_TIME = 6000;
  // The words the section writes as attributes of its [data-compare-strings] element.
  const WORDS = [
    'tabText',
    'limitNote',
    'gone',
    'removed',
    'cleared',
    'loadFailed',
    'tableFailed',
    'caption',
    'feature',
    'emptyCell',
    'removeNamed',
    'variantFailed',
    'variantShown',
  ];

  /** The stored list as items { id, handle, title }: valid, without duplicates, cut to the limit. */
  const parseItems = (list, limit) => {
    const seen = new Set();
    const items = [];
    for (const entry of list) {
      const id = String(entry?.id ?? '');
      const handle = typeof entry?.handle === 'string' ? entry.handle : '';
      if (!id || !handle || seen.has(id)) continue;
      seen.add(id);
      items.push({ id, handle, title: typeof entry.title === 'string' ? entry.title : '' });
    }
    return items.slice(0, limit);
  };

  /** Adds an item after the others: { items, status } where status is 'added', 'kept' (already there) or 'full'. */
  const addItem = (items, item, limit) => {
    if (items.some((entry) => entry.id === item.id)) return { items, status: 'kept' };
    if (items.length >= limit) return { items, status: 'full' };
    return { items: [...items, item], status: 'added' };
  };

  /** The address of a product's Section Rendering answer; the variant is asked for when a column chose one. */
  const requestUrl = (root, handle, section, variant = '') => {
    const sections = `${encodeURIComponent(section)},compare-tray`;
    const chosen = variant ? `&variant=${encodeURIComponent(variant)}` : '';
    return `${root}products/${encodeURIComponent(handle)}?sections=${sections}${chosen}`;
  };

  const shell = () => document.querySelector('product-compare');
  const keepEditorClean = () => Boolean(window.Shopify?.designMode);

  const store = {
    items: null,
    get limit() {
      return Number(shell()?.dataset.maxItems) || 4;
    },
    read() {
      if (keepEditorClean()) return (this.items ??= []);
      this.items = parseItems(safeStorage.read(ITEMS_KEY, { list: true }), this.limit);
      return this.items;
    },
    list() {
      return this.items ?? this.read();
    },
    has(id) {
      return this.list().some((entry) => entry.id === String(id));
    },
    write(items) {
      this.items = items;
      // Storage can be blocked; the selection then lasts for this page.
      if (!keepEditorClean()) safeStorage.write(ITEMS_KEY, items);
      publish(PUB_SUB_EVENTS.compareChange, { items }).catch(() => {});
    },
    add(item) {
      const result = addItem(this.list(), item, this.limit);
      if (result.status === 'added') this.write(result.items);
      return result.status;
    },
    remove(id) {
      if (this.has(id)) this.write(this.list().filter((entry) => entry.id !== String(id)));
    },
    clear() {
      this.write([]);
    },
  };

  /** Shows or hides the checkboxes and the tab, and tells everything that follows the selection. */
  const showMode = (on) => {
    if (on) document.documentElement.dataset.compareMode = 'on';
    else delete document.documentElement.dataset.compareMode;
    publish(PUB_SUB_EVENTS.compareChange, { items: store.list() }).catch(() => {});
  };

  const setMode = (on) => {
    showMode(on);
    if (!keepEditorClean()) safeStorage.set(MODE_KEY, on ? 'on' : 'off');
  };

  /** The checkbox on a card. It is shown by the page's data-compare-mode attribute. */
  class ComparePick extends HTMLElement {
    connectedCallback() {
      // Without the compare section on the page (removed from the overlays), the checkboxes stay hidden.
      if (!shell()) delete document.documentElement.dataset.compareMode;
      this.controller = new AbortController();
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.input.addEventListener('change', () => this.pick(), { signal: this.controller.signal });
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.compareChange, () => this.sync());
      this.sync();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
    }

    sync() {
      this.input.checked = store.has(this.dataset.productId);
    }

    pick() {
      const { productId: id, productHandle: handle, productTitle: title } = this.dataset;
      if (!this.input.checked) return store.remove(id);
      if (store.add({ id, handle, title }) === 'full') {
        this.input.checked = false;
        this.dispatchEvent(new CustomEvent('product-compare:full', { bubbles: true }));
      }
    }
  }

  /** The switch in the toolbar: it shows and hides the checkboxes, and the choice is remembered. */
  class CompareSwitch extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.hidden = !shell();
      this.input.checked = document.documentElement.dataset.compareMode === 'on';
      const { signal } = this.controller;
      this.input.addEventListener('change', () => setMode(this.input.checked), { signal });
      window.addEventListener(
        'storage',
        (event) => {
          if (event.key !== MODE_KEY) return;
          this.input.checked = event.newValue === 'on';
          showMode(this.input.checked);
        },
        { signal }
      );
      this.removeAttribute('data-pending');
    }

    disconnectedCallback() {
      this.controller?.abort();
    }
  }

  customElements.define('compare-pick', ComparePick);
  customElements.define('compare-switch', CompareSwitch);

  class ProductCompare extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.tab = this.querySelector('[data-compare-tab]');
      this.tray = this.querySelector('#CompareTray');
      this.cards = this.tray.querySelector('[data-compare-cards]');
      this.hint = this.tray.querySelector('[data-compare-hint]');
      this.loading = this.tray.querySelector('[data-compare-loading]');
      this.trayAnnouncer = this.tray.querySelector('[data-compare-announce]');
      this.pageAnnouncer = this.querySelector(':scope > [data-compare-announce]');
      this.compareNow = this.tray.querySelector('[data-compare-open-table]');
      this.windowHost = this.querySelector('#CompareTable').closest('dialog-window');
      this.toast = this.querySelector('#CompareLimit');
      const words = this.querySelector('[data-compare-strings]');
      this.strings = Object.fromEntries(WORDS.map((name) => [name, themeStrings(name, words)]));

      this.failure = this.tray.querySelector('[data-compare-failure]');
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.compareChange, () => this.render());
      this.tray.addEventListener('dialog-window:open', () => this.fillTray(), { signal });
      this.tray.addEventListener('dialog-window:close', () => this.trayRun?.abort(), { signal });
      this.tray.addEventListener('click', (event) => this.onTrayClick(event), { signal });
      this.querySelector('#CompareTable').addEventListener('dialog-window:close', () => this.tableElement?.reset(), {
        signal,
      });
      document.addEventListener('product-compare:full', () => this.showLimit(), { signal });
      window.addEventListener('storage', (event) => event.key === ITEMS_KEY && this.followOtherTab(), { signal });
      if (keepEditorClean()) this.listenToEditor(signal);
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
      this.trayRun?.abort();
      clearTimeout(this.toastTimer);
    }

    // ---- The tab and the counts ----

    render() {
      const count = store.list().length;
      const on = document.documentElement.dataset.compareMode === 'on';
      this.tab.hidden = !(on && count > 0);
      this.tab.textContent = this.strings.tabText?.replace('[n]', count) ?? '';
      for (const total of this.querySelectorAll('[data-compare-total]')) total.textContent = count;
      this.updateActions();
      if (count === 0 && this.tray.open) this.closeEmptyTray();
    }

    // With one product, the hint above Compare now says a second one is needed.
    updateActions() {
      const count = store.list().length;
      this.hint.hidden = count !== 1;
      this.compareNow.disabled = count < 2;
    }

    // A tray with nothing left to compare closes. The tab that opened it is hidden with the selection, so focus goes to
    // the switch, without scrolling the page.
    closeEmptyTray() {
      this.tray.close();
      document.querySelector('compare-switch input')?.focus({ preventScroll: true });
    }

    // While the tray is open the page is inert, so the tray has a status line of its own.
    announce(text) {
      (this.tray.open ? this.trayAnnouncer : this.pageAnnouncer).textContent = text;
    }

    followOtherTab() {
      store.read();
      publish(PUB_SUB_EVENTS.compareChange, { items: store.list() }).catch(() => {});
      if (this.tray.open) this.fillTray();
    }

    showLimit() {
      const message = this.toast.querySelector('.notice__message');
      message.textContent = this.strings.limitNote?.replace('[n]', store.limit) ?? '';
      this.toast.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => (this.toast.hidden = true), TOAST_TIME);
    }

    // ---- Asking for a product ----

    /**
     * The answer for a product and variant: { card, product } nodes, or { gone: true } for a 404 or a product the
     * section marks missing. The text of an answer that came is kept for the page (sectionAnswers); a 404 is not.
     */
    async answer(item, variant = '') {
      const section = this.dataset.section;
      const url = requestUrl(this.dataset.root, item.handle, section, variant);
      let text;
      try {
        text = await answers.get(url, AbortSignal.timeout(REQUEST_TIMEOUT));
      } catch (error) {
        if (error.status === 404) return { gone: true };
        throw error;
      }
      const data = JSON.parse(text);
      const read = (html) => (html ? new DOMParser().parseFromString(html, 'text/html') : null);
      const basket = read(data['compare-tray']);
      if (basket?.querySelector('[data-compare-missing]')) return { gone: true };
      return {
        card: basket?.querySelector('[data-compare-card]') ?? null,
        product: read(data[section])?.querySelector('[data-compare-product]') ?? null,
      };
    }

    // ---- The tray ----

    async fillTray() {
      this.trayRun?.abort();
      const run = (this.trayRun = new AbortController());
      const items = [...store.list()];
      this.cards.replaceChildren();
      this.loading.hidden = false;
      this.updateActions();
      const results = await Promise.all(
        items.map((item) =>
          this.answer(item).then(
            (answer) => ({ item, answer }),
            (error) => ({ item, error })
          )
        )
      );
      if (run.signal.aborted) return;
      this.loading.hidden = true;
      let gone = 0;
      for (const { item, answer, error } of results) {
        if (answer?.gone) {
          store.remove(item.id);
          gone++;
        } else if (answer?.card) {
          this.cards.append(document.importNode(answer.card, true));
        } else {
          if (error) console.warn('Core: a compared product could not be loaded', error);
          this.cards.append(this.failedCard(item));
        }
      }
      if (gone) this.announce(this.strings.gone);
      this.updateActions();
    }

    // The card of a product that could not be loaded: what happened, a way to try again and a way to drop it.
    failedCard(item) {
      const card = this.failure.content.firstElementChild.cloneNode(true);
      card.dataset.compareCard = item.id;
      card.querySelector('[data-compare-failed-text]').textContent = this.strings.loadFailed;
      const name = item.title || item.handle;
      card
        .querySelector('[data-compare-remove]')
        .setAttribute('aria-label', this.strings.removeNamed.replace('[product]', name));
      return card;
    }

    onTrayClick(event) {
      const target = event.target.closest('[data-compare-remove], [data-compare-retry], [data-compare-clear]');
      if (target?.matches('[data-compare-remove]')) this.removeCard(target.closest('[data-compare-card]'));
      else if (target?.matches('[data-compare-retry]')) this.fillTray();
      else if (target?.matches('[data-compare-clear]')) this.clear();
      else if (event.target.closest('[data-compare-open-table]')) this.openTable();
    }

    // Removing a card moves focus to the next card's remove button, else to the previous one's, else to the title. The
    // last card closes the tray.
    removeCard(card) {
      if (!card) return;
      const name = card.querySelector('.card-title')?.textContent.trim() ?? '';
      const neighbour = card.nextElementSibling ?? card.previousElementSibling;
      store.remove(card.dataset.compareCard);
      card.remove();
      this.announce(this.strings.removed.replace('[product]', name));
      if (!this.tray.open) return;
      const title = this.tray.querySelector('.dialog-window__title');
      const next = neighbour?.querySelector('[data-compare-remove]') ?? title;
      if (next === title) title.tabIndex = -1;
      next.focus();
    }

    /** The table calls this when a column's Remove is pressed: the product leaves the selection and the tray. */
    removeProduct(id) {
      store.remove(id);
      this.cards.querySelector(`[data-compare-card="${CSS.escape(id)}"]`)?.remove();
    }

    closeTable() {
      this.windowHost.close();
    }

    // Clearing empties the selection, which closes the tray.
    clear() {
      store.clear();
      this.announce(this.strings.cleared);
    }

    // ---- The table ----

    async openTable(items = store.list()) {
      this.tray.close();
      this.windowHost.open(this.tab);
      const failed = this.querySelector('#CompareTableFailed');
      const loading = this.querySelector('#CompareTable [data-compare-loading]');
      // Every opening starts from "loading": the files of an earlier opening that failed are asked for again.
      failed.hidden = true;
      loading.hidden = false;
      try {
        await Promise.all(this.dataset.files.split(',').filter(Boolean).map(loadAsset));
        // The files are loaded: a table that is still not defined means a file ran and threw, and waiting for it
        // would wait for good. A file that failed to download is asked for again on the next opening; one that loaded
        // and threw stays in the page, so the failed state shows again until the page is reloaded.
        if (!customElements.get('compare-table')) throw new Error('compare-table is not defined');
        this.tableElement = this.querySelector('compare-table');
        this.tableElement.show(this, items);
      } catch (error) {
        console.warn('Core: the comparison could not be opened', error);
        failed.querySelector('.notice__message').textContent = this.strings.tableFailed;
        failed.hidden = false;
        loading.hidden = true;
      }
    }

    // ---- The theme editor ----

    // Selecting the section opens the table, with the first products of the page when fewer than two are ticked, so the
    // rows can be seen while the blocks are edited; selecting anything else closes it.
    listenToEditor(signal) {
      const mine = (event) => event.target.id?.endsWith(this.dataset.section);
      const show = (event) => {
        if (!mine(event)) return;
        const own = store.list();
        const sample = [...document.querySelectorAll('compare-pick')]
          .slice(0, 3)
          .map((pick) => ({ id: pick.dataset.productId, handle: pick.dataset.productHandle }));
        this.openTable(own.length >= 2 ? own : sample);
      };
      document.addEventListener('shopify:section:select', show, { signal });
      document.addEventListener('shopify:block:select', show, { signal });
      document.addEventListener('shopify:section:deselect', (event) => mine(event) && this.windowHost.close(), {
        signal,
      });
    }
  }

  customElements.define('product-compare', ProductCompare);
}
